import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	subscribeToSubscriptions,
	addSubscription,
	updateSubscription,
	removeSubscription,
} from '../../../services/PersonalSubscriptionsService';
import {
	PERIODS,
	buildSubscriptionInput,
	formatMoney,
	monthlyEquivalent,
	rollup,
	sortSubscriptions,
} from '../../../services/personalSubscriptionsModel';

/* The Personal space's cancellation ledger: subscriptions that have been cut,
   and what cutting them saves.

   Ported from Anthony's "Meeting Desk" artifact (2026-07-29/30), where this
   grew as a side panel of a household finance meeting and outlived it. The
   artifact was browser-local; this reads and writes `personalSubscriptions`,
   owner-walled like every other Personal collection, so it is shared with
   Grace and survives a cleared browser.

   THREE PARTS, in the order the eye wants them:
   1. The summary — saved per month is the headline; per year and the count
      sit beside it.
   2. The entry row — name, amount, a month/year toggle, Enter to submit. The
      toggle PERSISTS between entries (a run of yearly cancellations should
      only need setting once), and nothing has to be typed to mean
      "cancelled": being on this list is what cancelled means.
   3. A collapsed "Manage N" disclosure holding the editable list — rename,
      change amount, flip period, remove. Each row shows its own monthly
      equivalent.

   CARET SAFETY. A row being edited keeps its draft in the row's OWN state and
   commits on blur / Enter. The headline re-renders from the Firestore snapshot
   when the write lands, never by rebuilding the input under the cursor — the
   failure the prototype had and the handoff names.

   A FAILED LOAD SHOWS THE FAILURE, never "Nothing cut yet": an empty ledger
   and a denied one look identical otherwise.

   `rowsOverride` is for harnesses only (the throwaway-route trick: a
   component rendered with injected data needs no auth and no Firebase). When
   given, the panel never subscribes; writes still go to the real service, so
   a harness must not click Add against prod. */

function PeriodToggle({ value, onChange, idBase }) {
	return (
		<div className='psub-period' role='group' aria-label='Billing period'>
			{PERIODS.map((p) => (
				<button
					key={p}
					type='button'
					id={`${idBase}-${p}`}
					className={`psub-period__btn${value === p ? ' is-on' : ''}`}
					aria-pressed={value === p}
					onClick={() => onChange(p)}>
					{p === 'month' ? '/ month' : '/ year'}
				</button>
			))}
		</div>
	);
}

/* One editable row inside Manage. Draft state is local so the snapshot that
   follows a save cannot rebuild the input mid-keystroke. */
function SubscriptionRow({ row, onSave, onRemove }) {
	const [name, setName] = useState(row.name);
	const [amount, setAmount] = useState(String(row.amount));
	const [error, setError] = useState(null);
	const [confirming, setConfirming] = useState(false);

	// A change that arrived from elsewhere (Grace, another tab) replaces an
	// untouched draft; a draft mid-edit is left alone.
	const touched = useRef(false);
	useEffect(() => {
		if (touched.current) return;
		setName(row.name);
		setAmount(String(row.amount));
	}, [row.name, row.amount]);

	const commit = async (changes) => {
		const { error: err } = buildSubscriptionInput({
			name: changes.name ?? name,
			amount: changes.amount ?? amount,
			period: changes.period ?? row.period,
		});
		if (err) {
			setError(err);
			return;
		}
		setError(null);
		try {
			await onSave(row.id, changes);
			touched.current = false;
		} catch (e) {
			setError(e?.message || 'Could not save that change.');
		}
	};

	const commitName = () => {
		if (name.trim() === row.name) return;
		commit({ name });
	};
	const commitAmount = () => {
		if (Number(amount) === row.amount) return;
		commit({ amount });
	};
	const onKey = (fn) => (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			fn();
			e.currentTarget.blur();
		}
	};

	return (
		<li className='psub-row'>
			<div className='psub-row__main'>
				<input
					className='psub-input psub-row__name'
					aria-label='Subscription name'
					value={name}
					onChange={(e) => {
						touched.current = true;
						setName(e.target.value);
					}}
					onBlur={commitName}
					onKeyDown={onKey(commitName)}
				/>
				<span className='psub-row__amount'>
					<span className='psub-input__prefix' aria-hidden='true'>
						$
					</span>
					<input
						className='psub-input psub-row__amt'
						aria-label='Amount'
						inputMode='decimal'
						value={amount}
						onChange={(e) => {
							touched.current = true;
							setAmount(e.target.value);
						}}
						onBlur={commitAmount}
						onKeyDown={onKey(commitAmount)}
					/>
				</span>
				<PeriodToggle
					idBase={`psub-${row.id}`}
					value={row.period}
					onChange={(p) => p !== row.period && commit({ period: p })}
				/>
				<span className='psub-row__monthly' title='What this saves per month'>
					{formatMoney(monthlyEquivalent(row))}
					<span className='psub-row__monthly-unit'> / mo</span>
				</span>
				{confirming ? (
					<span className='psub-row__confirm'>
						<button
							type='button'
							className='btn-cyv-ghost psub-btn psub-btn--danger'
							onClick={() => onRemove(row.id)}>
							Remove
						</button>
						<button
							type='button'
							className='btn-cyv-ghost psub-btn'
							onClick={() => setConfirming(false)}>
							Keep
						</button>
					</span>
				) : (
					<button
						type='button'
						className='psub-row__remove'
						aria-label={`Remove ${row.name}`}
						title='Remove'
						onClick={() => setConfirming(true)}>
						<i className='fa-sharp fa-solid fa-xmark' aria-hidden='true' />
					</button>
				)}
			</div>
			{error && (
				<div className='psub-error' role='alert'>
					{error}
				</div>
			)}
		</li>
	);
}

function SubscriptionsPanel({ rowsOverride = null }) {
	const [liveRows, setRows] = useState([]);
	const [loading, setLoading] = useState(!rowsOverride);
	const [loadError, setLoadError] = useState(null);
	const rows = rowsOverride || liveRows;

	useEffect(
		() =>
			rowsOverride
				? undefined
				: subscribeToSubscriptions(
				(next) => {
					setRows(next);
					setLoading(false);
					setLoadError(null);
				},
				(err) => {
					setLoadError(err);
					setLoading(false);
				}
			),
		[rowsOverride]
	);

	const summary = useMemo(() => rollup(rows), [rows]);
	const sorted = useMemo(() => sortSubscriptions(rows), [rows]);

	// Entry row
	const [name, setName] = useState('');
	const [amount, setAmount] = useState('');
	const [period, setPeriod] = useState('month'); // persists between entries
	const [entryError, setEntryError] = useState(null);
	const [busy, setBusy] = useState(false);
	const nameRef = useRef(null);

	const submit = async (e) => {
		e?.preventDefault?.();
		if (busy) return;
		const { doc, error } = buildSubscriptionInput({ name, amount, period });
		if (error) {
			setEntryError(error);
			return;
		}
		setBusy(true);
		setEntryError(null);
		try {
			await addSubscription(doc);
			setName('');
			setAmount('');
			nameRef.current?.focus();
		} catch (err) {
			setEntryError(err?.message || 'Could not save that cancellation.');
		} finally {
			setBusy(false);
		}
	};

	const [manageOpen, setManageOpen] = useState(false);

	const count = summary.count;
	const manageLabel = `Manage ${count} ${count === 1 ? 'cancellation' : 'cancellations'}`;

	return (
		<section className='psp-slot psub' aria-labelledby='psub-title'>
			<header className='psp-slot__head'>
				<i className='fa-sharp-duotone fa-solid fa-scissors psp-slot__head-icon' aria-hidden='true' />
				<span className='psp-slot__head-name' id='psub-title'>
					Subscriptions cut
				</span>
				{count > 0 && <span className='psp-slot__count'>{count}</span>}
			</header>

			{loadError ? (
				<div className='psp-shell-error psub-loaderror' role='status'>
					<i className='fa-sharp fa-solid fa-triangle-exclamation' aria-hidden='true' />
					<span>Couldn't load the subscriptions ledger.</span>
				</div>
			) : (
				<>
					<div className='psub-summary'>
						<div className='psub-stat psub-stat--lead'>
							<span className='psub-stat__value'>{formatMoney(summary.monthly)}</span>
							<span className='psub-stat__label'>saved per month</span>
						</div>
						<div className='psub-stat'>
							<span className='psub-stat__value'>{formatMoney(summary.annual)}</span>
							<span className='psub-stat__label'>per year</span>
						</div>
						<div className='psub-stat'>
							<span className='psub-stat__value'>{count}</span>
							<span className='psub-stat__label'>
								{count === 1 ? 'cancellation' : 'cancellations'}
							</span>
						</div>
					</div>

					<form className='psub-entry' onSubmit={submit}>
						<label className='psub-entry__field psub-entry__field--name'>
							<span className='psub-entry__label'>Subscription</span>
							<input
								ref={nameRef}
								className='psub-input'
								placeholder='e.g. Peacock'
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</label>
						<label className='psub-entry__field psub-entry__field--amount'>
							<span className='psub-entry__label'>Cost</span>
							<span className='psub-entry__amount'>
								<span className='psub-input__prefix' aria-hidden='true'>
									$
								</span>
								<input
									className='psub-input'
									inputMode='decimal'
									placeholder='0.00'
									value={amount}
									onChange={(e) => setAmount(e.target.value)}
								/>
							</span>
						</label>
						<div className='psub-entry__field psub-entry__field--period'>
							<span className='psub-entry__label'>Billed</span>
							<PeriodToggle idBase='psub-entry' value={period} onChange={setPeriod} />
						</div>
						<button
							type='submit'
							className='btn-cyv-teal psub-btn psub-entry__submit'
							disabled={busy}>
							<i className='fa-sharp fa-solid fa-plus' aria-hidden='true' />
							Add
						</button>
						{entryError && (
							<div className='psub-error psub-entry__error' role='alert'>
								{entryError}
							</div>
						)}
					</form>

					{!loading && count === 0 && (
						<div className='psub-empty'>
							Nothing cut yet. Add a subscription you have cancelled and the
							savings total starts here.
						</div>
					)}

					{count > 0 && (
						<div className='psub-manage'>
							<button
								type='button'
								className='psub-manage__toggle'
								aria-expanded={manageOpen}
								onClick={() => setManageOpen((o) => !o)}>
								<i
									className={`fa-sharp fa-solid ${
										manageOpen ? 'fa-chevron-down' : 'fa-chevron-right'
									}`}
									aria-hidden='true'
								/>
								{manageLabel}
							</button>
							{manageOpen && (
								<ul className='psub-list'>
									{sorted.map((row) => (
										<SubscriptionRow
											key={row.id}
											row={row}
											onSave={updateSubscription}
											onRemove={removeSubscription}
										/>
									))}
								</ul>
							)}
						</div>
					)}
				</>
			)}
		</section>
	);
}

export default SubscriptionsPanel;
